import "./updaterate.css";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API from "src/API2";
import toast from "react-hot-toast";

/* ── Inline icons ───────────────────────────────────────────────── */
const Svg = ({ children, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IcArrow  = () => <Svg><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></Svg>;
const IcTag    = () => <Svg><path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></Svg>;
const IcCal    = () => <Svg><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></Svg>;
const IcDoc    = () => <Svg><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></Svg>;
const IcStar   = () => <Svg><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></Svg>;
const IcCheck  = () => <Svg><polyline points="20 6 9 17 4 12" /></Svg>;
const IcPlus   = () => <Svg size={14}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></Svg>;
const IcInfo   = () => <Svg size={14}><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></Svg>;

const DAYS = [
  { idx: 0, short: "Su", full: "Sunday" },
  { idx: 1, short: "Mo", full: "Monday" },
  { idx: 2, short: "Tu", full: "Tuesday" },
  { idx: 3, short: "We", full: "Wednesday" },
  { idx: 4, short: "Th", full: "Thursday" },
  { idx: 5, short: "Fr", full: "Friday" },
  { idx: 6, short: "Sa", full: "Saturday" },
];

const fmtDateLong = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
};

const UpdateRate = () => {
  const navigate = useNavigate();
  const { rateId } = useParams();

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [code, setCode] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [workingDays, setWorkingDays] = useState([0, 1, 2, 3, 4, 5, 6]);
  const [services, setServices] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  /* ── Load rate + services ── */
  useEffect(() => {
    setLoading(true);
    API.get(`rates/${rateId}`)
      .then((resp) => {
        const rate = resp.data.rate;
        setCode(rate.code || "");
        setStartDate(new Date(rate.start_date));
        setEndDate(new Date(rate.end_date));
        setDescription(rate.description || "");
        try { setWorkingDays(JSON.parse(rate.work_days) || []); }
        catch { setWorkingDays([]); }
        setServices(rate.services || []);
        setCategory(rate.type || "");
      })
      .catch(() => toast.error("Could not load rate"))
      .finally(() => setLoading(false));

    toast.promise(
      API.get("roomservices").then((resp) => setAllServices(resp.data.services || [])),
      {
        loading: "Loading services…",
        success: "Services loaded",
        error: "Could not load services",
      },
    );
  }, [rateId]);

  const toggleDay = (i) => {
    setWorkingDays((prev) => (prev.includes(i) ? prev.filter((d) => d !== i) : [...prev, i].sort()));
  };

  const toggleService = (s) => {
    setServices((prev) =>
      prev.find((x) => x.id === s.id) ? prev.filter((x) => x.id !== s.id) : [...prev, s],
    );
  };
  const isServiceSelected = (s) => services.some((x) => x.id === s.id);

  const setQuickDays = (preset) => {
    if (preset === "all") setWorkingDays([0, 1, 2, 3, 4, 5, 6]);
    else if (preset === "weekdays") setWorkingDays([1, 2, 3, 4, 5]);
    else if (preset === "weekend") setWorkingDays([0, 6]);
    else if (preset === "none") setWorkingDays([]);
  };

  const canSubmit = !submitting && category && startDate && endDate;

  const update = () => {
    if (!canSubmit) {
      toast.error("Type and dates are required.");
      return;
    }
    setSubmitting(true);
    toast.promise(
      API.put(`rates/${rateId}`, {
        code,
        description,
        start_date: startDate,
        end_date: endDate,
        work_days: JSON.stringify(workingDays),
        type: category,
        services: services.map((e) => e.id),
      })
        .then(() => navigate("/rates"))
        .finally(() => setSubmitting(false)),
      {
        loading: "Updating rate…",
        success: "Rate updated",
        error: "Could not update rate",
      },
    );
  };

  const dayLabels = useMemo(() => {
    if (workingDays.length === 7) return "Every day";
    if (workingDays.length === 0) return "No days selected";
    return workingDays
      .slice()
      .sort()
      .map((i) => DAYS[i].short)
      .join(" · ");
  }, [workingDays]);

  return (
    <div className="cr2-page">
      {/* ── Hero ── */}
      <header className="cr2-hero">
        <button type="button" className="cr2-back" onClick={() => navigate("/rates")}>
          <IcArrow /> Back to rates
        </button>
        <div className="cr2-hero__body">
          <span className="cr2-hero__eyebrow">Edit · {code || "Rate"}</span>
          <h1 className="cr2-hero__title">Update rate</h1>
          <p className="cr2-hero__sub">
            Adjust validity, working days, bundled services and description. The
            rate code is locked once a rate has been created.
          </p>
        </div>
      </header>

      <div className="cr2-grid">
        {/* ── Form column ── */}
        <div className="cr2-form">
          {/* Identity */}
          <section className="cr2-card">
            <header className="cr2-card__head">
              <span className="cr2-card__icon"><IcTag /></span>
              <div>
                <h3>Identity</h3>
                <p>Rate code is locked. You can still change its type.</p>
              </div>
            </header>
            <div className="cr2-grid-2">
              <label className="cr2-field">
                <span>Code</span>
                <input value={code} disabled />
                <span className="cr2-counter">Cannot be changed</span>
              </label>
              <label className="cr2-field">
                <span>Type</span>
                <select value={category} onChange={(e) => setCategory(e.target.value)}>
                  <option value="" disabled>Choose type</option>
                  <option value="RATE">Rate — baseline pricing</option>
                  <option value="OFFER">Offer — promotion</option>
                </select>
              </label>
            </div>
          </section>

          {/* Validity */}
          <section className="cr2-card">
            <header className="cr2-card__head">
              <span className="cr2-card__icon cr2-card__icon--blue"><IcCal /></span>
              <div>
                <h3>Validity window</h3>
                <p>When this rate can be sold.</p>
              </div>
            </header>
            <div className="cr2-grid-2">
              <label className="cr2-field">
                <span>Start date</span>
                <DatePicker
                  selected={startDate}
                  onChange={(d) => setStartDate(d)}
                  dateFormat="dd/MM/yyyy"
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                />
              </label>
              <label className="cr2-field">
                <span>End date</span>
                <DatePicker
                  selected={endDate}
                  onChange={(d) => setEndDate(d)}
                  dateFormat="dd/MM/yyyy"
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                />
              </label>
            </div>
          </section>

          {/* Working days */}
          <section className="cr2-card">
            <header className="cr2-card__head">
              <span className="cr2-card__icon cr2-card__icon--orange"><IcCal /></span>
              <div>
                <h3>Working days</h3>
                <p>The days of the week this rate is bookable.</p>
              </div>
            </header>
            <div className="cr2-day-row">
              {DAYS.map((d) => {
                const selected = workingDays.includes(d.idx);
                return (
                  <button
                    type="button"
                    key={d.idx}
                    className={`cr2-day ${selected ? "is-on" : ""}`}
                    onClick={() => toggleDay(d.idx)}
                    title={d.full}
                  >
                    <span className="cr2-day__short">{d.short}</span>
                    <span className="cr2-day__full">{d.full}</span>
                  </button>
                );
              })}
            </div>
            <div className="cr2-quick">
              <button type="button" className="cr2-mini-pill" onClick={() => setQuickDays("all")}>Every day</button>
              <button type="button" className="cr2-mini-pill" onClick={() => setQuickDays("weekdays")}>Weekdays</button>
              <button type="button" className="cr2-mini-pill" onClick={() => setQuickDays("weekend")}>Weekends</button>
              <button type="button" className="cr2-mini-pill" onClick={() => setQuickDays("none")}>None</button>
            </div>
          </section>

          {/* Services */}
          <section className="cr2-card">
            <header className="cr2-card__head">
              <span className="cr2-card__icon cr2-card__icon--green"><IcStar /></span>
              <div>
                <h3>Bundled services</h3>
                <p>Services included with this rate.</p>
              </div>
            </header>
            {allServices.length === 0 ? (
              <div className="cr2-info">
                <IcInfo /> <span>No services available yet.</span>
              </div>
            ) : (
              <div className="cr2-services">
                {allServices.map((s) => {
                  const selected = isServiceSelected(s);
                  return (
                    <button
                      type="button"
                      key={s.id}
                      className={`cr2-service ${selected ? "is-on" : ""}`}
                      onClick={() => toggleService(s)}
                    >
                      {selected ? <IcCheck /> : <IcPlus />}
                      <span>{s.name}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {/* Description */}
          <section className="cr2-card">
            <header className="cr2-card__head">
              <span className="cr2-card__icon cr2-card__icon--orange"><IcDoc /></span>
              <div>
                <h3>Description</h3>
                <p>Shown to guests on the booking site.</p>
              </div>
            </header>
            <label className="cr2-field">
              <span>Description</span>
              <textarea
                rows={4}
                placeholder="What does this rate include? Any conditions worth highlighting?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <span className="cr2-counter">{description.length} characters</span>
            </label>
          </section>
        </div>

        {/* ── Sticky preview ── */}
        <aside className="cr2-summary">
          <div className="cr2-summary__card">
            <span className="cr2-summary__eyebrow">Live preview</span>
            <h3>How it will look</h3>

            <div className="cr2-preview">
              <header className="cr2-preview__head">
                <span className={`cr2-type cr2-type--${(category || "rate").toLowerCase()}`}>
                  {category || "TYPE"}
                </span>
                <span className="cr2-preview__code">{code || "CODE"}</span>
              </header>
              <p className="cr2-preview__desc">
                {description || "Describe what this rate includes for the guest."}
              </p>
              <ul className="cr2-summary__list">
                <li>
                  <span><IcCal /> Validity</span>
                  <strong>{fmtDateLong(startDate)} → {fmtDateLong(endDate)}</strong>
                </li>
                <li>
                  <span><IcCal /> Working days</span>
                  <strong>{dayLabels}</strong>
                </li>
                <li>
                  <span><IcStar /> Services</span>
                  <strong>{services.length} bundled</strong>
                </li>
              </ul>

              {services.length > 0 && (
                <div className="cr2-preview__chips">
                  {services.slice(0, 8).map((s) => (
                    <span key={s.id} className="cr2-chip">{s.name}</span>
                  ))}
                  {services.length > 8 && (
                    <span className="cr2-chip cr2-chip--more">+{services.length - 8}</span>
                  )}
                </div>
              )}
            </div>

            <div className="cr2-summary__actions">
              <button
                type="button"
                className="cr2-btn cr2-btn--ghost"
                onClick={() => navigate("/rates")}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cr2-btn cr2-btn--primary"
                onClick={update}
                disabled={!canSubmit || loading}
              >
                {submitting ? "Saving…" : (<><IcCheck /> Save changes</>)}
              </button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default UpdateRate;
